import { Cookie, FileCheck2, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "October 9, 2026";

type LegalSection = {
  id: string;
  title: string;
  content: ReactNode;
};

type LegalPageProps = {
  eyebrow: string;
  title: string;
  summary: string;
  icon: ReactNode;
  sections: LegalSection[];
};

function useLegalDocumentMetadata(title: string, description: string) {
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionMeta?.content;

    document.title = `${title} | Mero Perks`;
    if (descriptionMeta) descriptionMeta.content = description;

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== undefined) {
        descriptionMeta.content = previousDescription;
      }
    };
  }, [description, title]);
}

function LegalPage({ eyebrow, title, summary, icon, sections }: LegalPageProps) {
  useLegalDocumentMetadata(title, summary);

  return (
    <main className="legal-page">
      <header className="legal-hero">
        <div className="container legal-hero__inner">
          <span className="legal-hero__icon" aria-hidden="true">
            {icon}
          </span>
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1>{title}</h1>
            <p>{summary}</p>
            <small>Effective date: {EFFECTIVE_DATE}</small>
          </div>
        </div>
      </header>

      <div className="container legal-layout">
        <aside className="legal-toc" aria-label={`${title} contents`}>
          <strong>On this page</strong>
          <nav>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>
                {section.title}
              </a>
            ))}
          </nav>
        </aside>

        <article className="legal-document">
          <div className="legal-notice">
            <ShieldCheck aria-hidden="true" />
            <p>
              This page applies to the Mero Perks website, Android application, customer accounts,
              and Business Owner services unless a section says otherwise.
            </p>
          </div>
          {sections.map((section, index) => (
            <section id={section.id} key={section.id} aria-labelledby={`${section.id}-title`}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h2 id={`${section.id}-title`}>{section.title}</h2>
                {section.content}
              </div>
            </section>
          ))}
          <LegalContact />
          <RelatedLegalPages currentTitle={title} />
        </article>
      </div>
    </main>
  );
}

function LegalContact() {
  return (
    <section className="legal-contact" aria-labelledby="legal-contact-title">
      <span aria-hidden="true">
        <Mail />
      </span>
      <div>
        <h2 id="legal-contact-title">Questions or privacy requests</h2>
        <p>
          Contact the Mero Perks privacy team from the email address connected to your account so we
          can verify and respond securely.
        </p>
        <a href="mailto:privacy@meroperks.com">privacy@meroperks.com</a>
      </div>
    </section>
  );
}

const relatedPages = [
  { title: "Privacy Policy", to: "/privacy-policy" },
  { title: "Cookie Policy", to: "/cookies-policy" },
  { title: "Terms & Conditions", to: "/terms-and-conditions" },
  { title: "Account Deletion", to: "/account-deletion" },
];

function RelatedLegalPages({ currentTitle }: { currentTitle: string }) {
  return (
    <nav className="legal-related" aria-label="Related legal pages">
      <strong>Related information</strong>
      <div>
        {relatedPages
          .filter((page) => page.title !== currentTitle)
          .map((page) => (
            <Link key={page.to} to={page.to}>
              {page.title}
            </Link>
          ))}
      </div>
    </nav>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalPage
      eyebrow="Your information, clearly explained"
      title="Privacy Policy"
      summary="How Mero Perks collects, uses, shares, protects, and gives you control over your information."
      icon={<ShieldCheck />}
      sections={[
        {
          id: "information-we-collect",
          title: "Information we collect",
          content: (
            <>
              <p>
                We collect information you provide, information created when you use Mero Perks, and
                limited technical information needed to operate the service.
              </p>
              <ul>
                <li>
                  <strong>Account details:</strong> name, email address, password credentials, phone
                  number, birthday, address, and communication preferences.
                </li>
                <li>
                  <strong>Loyalty activity:</strong> membership ID, Business Reward Points, Tier
                  Points, tier status, Mero Credits, purchases, receipts, reward issuance,
                  redemptions, and one-time QR activity.
                </li>
                <li>
                  <strong>Business Owner details:</strong> business and legal name, PAN number,
                  contact details, locations, staff access, earning rules, offers, and approval
                  records.
                </li>
                <li>
                  <strong>Support information:</strong> messages, attachments, and evidence you
                  submit when asking for help.
                </li>
                <li>
                  <strong>Device and usage information:</strong> IP address, browser or app version,
                  device type, security events, and service diagnostics.
                </li>
                <li>
                  <strong>Optional permissions:</strong> camera access when you choose to scan a QR
                  code. Mero Perks does not require continuous camera access.
                </li>
              </ul>
            </>
          ),
        },
        {
          id: "how-we-use-data",
          title: "How we use information",
          content: (
            <>
              <p>We use information to provide and secure Mero Perks, including to:</p>
              <ul>
                <li>
                  create and verify accounts, authenticate sign-ins, and manage account roles;
                </li>
                <li>
                  calculate balances and tiers, issue rewards, process redemptions, and maintain
                  accurate ledgers;
                </li>
                <li>
                  connect customers with participating Business Owners and display relevant offers;
                </li>
                <li>
                  send verification, security, transactional, service, and opted-in marketing
                  messages;
                </li>
                <li>prevent duplicate redemptions, misuse, fraud, and unauthorized access;</li>
                <li>
                  provide support, resolve disputes, improve reliability, and meet legal
                  obligations.
                </li>
              </ul>
            </>
          ),
        },
        {
          id: "sharing",
          title: "When information is shared",
          content: (
            <>
              <p>We do not sell personal information. We share only what is necessary with:</p>
              <ul>
                <li>
                  <strong>Participating Business Owners</strong> when they need to recognize
                  membership, award points, validate a purchase, or redeem an offer. A Business
                  Owner does not receive personal details encoded inside a one-time QR code.
                </li>
                <li>
                  <strong>Service providers</strong> that support hosting, database, security, bot
                  protection, and transactional email delivery, including Cloudflare and Resend.
                </li>
                <li>
                  <strong>Authorities or professional advisers</strong> when required by law, needed
                  to protect users and the platform, or necessary to establish or defend legal
                  rights.
                </li>
                <li>
                  <strong>A successor organization</strong> as part of a merger, financing,
                  reorganization, or transfer of the service, subject to appropriate safeguards.
                </li>
              </ul>
            </>
          ),
        },
        {
          id: "retention",
          title: "Retention and deletion",
          content: (
            <>
              <p>
                We keep personal information only for as long as needed to provide the service,
                protect accounts, settle transactions, resolve disputes, and meet legal, tax,
                accounting, and audit requirements.
              </p>
              <p>
                When an account is deleted, we remove or de-identify information that is no longer
                required. Some transaction, redemption, security, and compliance records may be
                retained for the period required by law or legitimate recordkeeping needs. See the{" "}
                <Link to="/account-deletion">Account Deletion page</Link> for details.
              </p>
            </>
          ),
        },
        {
          id: "security",
          title: "Security and international processing",
          content: (
            <>
              <p>
                We use access controls, encrypted connections, restricted administrative
                permissions, expiring sessions, and single-use QR protections. No system can
                guarantee absolute security, so keep your password private and report suspicious
                activity promptly.
              </p>
              <p>
                Service providers may process information outside your country. Where this occurs,
                we use contractual and technical safeguards appropriate to the information and
                service.
              </p>
            </>
          ),
        },
        {
          id: "your-choices",
          title: "Your choices and rights",
          content: (
            <>
              <p>
                Depending on applicable law, you may ask to access, correct, export, restrict,
                object to, or delete your personal information. You can update profile and
                communication preferences from your account. You may unsubscribe from optional
                marketing without stopping essential account messages.
              </p>
              <p>
                We may need to verify your identity before completing a request. You may also have
                the right to complain to the relevant data-protection or consumer authority.
              </p>
            </>
          ),
        },
        {
          id: "children-and-changes",
          title: "Children and policy changes",
          content: (
            <>
              <p>
                Mero Perks is not directed to children who cannot legally consent to an online
                account. A parent or guardian should contact us if a child provided information
                without appropriate permission.
              </p>
              <p>
                We may update this policy as the platform or law changes. We will post the new
                effective date and provide additional notice when a material change requires it.
              </p>
            </>
          ),
        },
      ]}
    />
  );
}

export function CookiePolicyPage() {
  return (
    <LegalPage
      eyebrow="Simple, essential, transparent"
      title="Cookie Policy"
      summary="What Mero Perks stores in your browser, why it is needed, and the controls available to you."
      icon={<Cookie />}
      sections={[
        {
          id: "what-cookies-are",
          title: "Cookies and similar storage",
          content: (
            <p>
              Cookies are small values stored by a website in your browser. Mero Perks also uses
              local storage, which keeps preferences on your device. These technologies help the
              site remember a secure session and choices you make.
            </p>
          ),
        },
        {
          id: "what-we-use",
          title: "What Mero Perks uses",
          content: (
            <div
              className="legal-table-wrap"
              tabIndex={0}
              role="region"
              aria-label="Cookie and local storage details. Scroll horizontally to view every column."
            >
              <table>
                <thead>
                  <tr>
                    <th>Name or category</th>
                    <th>Purpose</th>
                    <th>Typical duration</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <code>loyalty_session</code>
                    </td>
                    <td>
                      Keeps an authenticated customer, Business Owner, or administrator securely
                      signed in.
                    </td>
                    <td>Up to 12 hours, or until logout</td>
                    <td>Strictly necessary cookie</td>
                  </tr>
                  <tr>
                    <td>Marketplace preferences</td>
                    <td>
                      Remembers list or map view, selected business types, and saved businesses on
                      that browser.
                    </td>
                    <td>Until cleared</td>
                    <td>Local storage</td>
                  </tr>
                  <tr>
                    <td>Cloudflare security and Turnstile</td>
                    <td>Protects forms and services from bots, fraud, and abusive traffic.</td>
                    <td>Varies by security event</td>
                    <td>Strictly necessary security storage</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ),
        },
        {
          id: "no-advertising-cookies",
          title: "Advertising and analytics",
          content: (
            <p>
              Mero Perks does not currently use third-party advertising cookies or cross-site
              behavioral tracking cookies. If non-essential analytics or advertising cookies are
              introduced, we will update this policy and provide consent controls where required.
            </p>
          ),
        },
        {
          id: "your-controls",
          title: "Your controls",
          content: (
            <>
              <p>
                You can remove cookies and local storage through your browser or device settings.
                Blocking the strictly necessary session cookie will prevent account sign-in and
                authenticated features from working. Clearing local storage resets marketplace
                preferences but does not delete your Mero Perks account.
              </p>
              <p>
                For account deletion, use the separate{" "}
                <Link to="/account-deletion">Account Deletion page</Link>.
              </p>
            </>
          ),
        },
        {
          id: "cookie-changes",
          title: "Changes to this policy",
          content: (
            <p>
              We may update this policy when browser storage, service providers, or legal
              requirements change. The effective date at the top shows the latest revision.
            </p>
          ),
        },
      ]}
    />
  );
}

export function TermsAndConditionsPage() {
  return (
    <LegalPage
      eyebrow="The rules behind every perk"
      title="Terms & Conditions"
      summary="The agreement governing customer accounts, Business Owner services, rewards, points, Mero Credits, and use of Mero Perks."
      icon={<FileCheck2 />}
      sections={[
        {
          id: "acceptance",
          title: "Acceptance and eligibility",
          content: (
            <>
              <p>
                By creating an account or using Mero Perks, you agree to these terms and the Privacy
                Policy. If you use Mero Perks for a business, you confirm that you are authorized to
                bind that business.
              </p>
              <p>
                You must be legally capable of entering this agreement. A minor may use the service
                only with permission and supervision from a parent or legal guardian where permitted
                by law.
              </p>
            </>
          ),
        },
        {
          id: "accounts",
          title: "Accounts and security",
          content: (
            <ul>
              <li>Provide accurate information and keep it current.</li>
              <li>
                Keep credentials and devices secure, and notify us promptly of suspected
                unauthorized access.
              </li>
              <li>
                One email may identify separate customer and Business Owner roles, but duplicate
                accounts within the same role are not allowed.
              </li>
              <li>
                You are responsible for activity performed through your account unless prohibited by
                applicable law.
              </li>
            </ul>
          ),
        },
        {
          id: "loyalty-balances",
          title: "Points, tiers, and Mero Credits",
          content: (
            <ul>
              <li>
                <strong>Business Reward Points</strong> belong to the issuing Business Owner
                program, are not cash, and generally cannot be transferred between businesses.
              </li>
              <li>
                <strong>Tier Points</strong> measure platform status only. They cannot be spent,
                transferred, or exchanged for cash.
              </li>
              <li>
                <strong>Mero Credits</strong> are a separate platform balance for eligible
                transactions and are not a bank deposit, stored-value bank account, or legal tender.
                Promotional and purchased balances may have different expiry, refund, and usage
                rules.
              </li>
              <li>
                Balances may be corrected when a purchase is reversed, entered incorrectly,
                duplicated, fraudulent, or otherwise ineligible.
              </li>
            </ul>
          ),
        },
        {
          id: "offers-and-redemption",
          title: "Offers, coupons, and redemption",
          content: (
            <>
              <p>
                Each reward is subject to its displayed eligibility, dates, location, tier,
                quantity, and usage limits. A reward issued by one Business Owner may be redeemed
                only with that issuing Business Owner unless the offer expressly says otherwise.
              </p>
              <p>
                Coupon and membership QR codes may be short-lived, single-use, and bound to the
                account and issuing business. Copying, selling, altering, or attempting to reuse a
                QR code is prohibited. Once a coupon is confirmed as redeemed, it cannot be used
                again.
              </p>
            </>
          ),
        },
        {
          id: "business-owners",
          title: "Business Owner responsibilities",
          content: (
            <p>
              Business Owners are responsible for lawful offers, accurate prices and descriptions,
              staff access, taxes, fulfillment, customer service, and honoring valid rewards they
              issue. Mero Perks may review, reject, suspend, or remove a Business Owner or offer
              that creates legal, security, financial, or customer harm.
            </p>
          ),
        },
        {
          id: "acceptable-use",
          title: "Acceptable use",
          content: (
            <p>
              You may not interfere with the service, probe security controls, automate abusive
              requests, impersonate another person, falsify purchases or receipts, manipulate
              balances, duplicate redemptions, introduce malicious code, or use Mero Perks for
              unlawful activity. We may investigate and reverse affected transactions.
            </p>
          ),
        },
        {
          id: "availability-and-liability",
          title: "Availability, disclaimers, and liability",
          content: (
            <>
              <p>
                We work to keep Mero Perks reliable, but availability may be interrupted by
                maintenance, connectivity, security events, or circumstances outside our control.
                Participating Business Owners remain responsible for the goods and services they
                provide.
              </p>
              <p>
                To the fullest extent permitted by law, Mero Perks is provided without guarantees
                beyond those expressly stated, and our liability is limited to direct losses
                reasonably caused by our breach. Nothing in these terms excludes consumer rights or
                liability that cannot legally be excluded.
              </p>
            </>
          ),
        },
        {
          id: "suspension-and-ending",
          title: "Suspension, termination, and deletion",
          content: (
            <p>
              We may restrict or suspend access to protect users, investigate misuse, comply with
              law, or address a material breach. You may stop using Mero Perks and request deletion
              at any time. Outstanding redemptions, disputes, refundable purchased balances, and
              legally required records may need to be resolved before final closure.
            </p>
          ),
        },
        {
          id: "law-and-changes",
          title: "Governing law and changes",
          content: (
            <p>
              These terms are governed by the laws of Nepal, without overriding mandatory consumer
              protections that apply where you live. Disputes will be handled by courts with
              competent jurisdiction in Nepal unless applicable law provides another forum. We may
              update these terms and will post a revised effective date and any notice required for
              material changes.
            </p>
          ),
        },
      ]}
    />
  );
}

export function AccountDeletionPage() {
  const deletionEmail =
    "mailto:privacy@meroperks.com?subject=Mero%20Perks%20account%20deletion%20request&body=Please%20delete%20my%20Mero%20Perks%20account.%0A%0ARegistered%20email%3A%20%0AAccount%20type%20(customer%20or%20Business%20Owner)%3A%20%0AFull%20name%3A%20";

  return (
    <LegalPage
      eyebrow="You control your account"
      title="Account Deletion"
      summary="How to request deletion of a Mero Perks customer or Business Owner account and what happens afterward."
      icon={<Trash2 />}
      sections={[
        {
          id: "request-deletion",
          title: "Submit a deletion request",
          content: (
            <>
              <p>
                Send the request from the email address registered to the account. Include your full
                name and whether the account is a customer or Business Owner account.
              </p>
              <div className="deletion-actions">
                <a className="button button--primary" href={deletionEmail}>
                  <Mail /> Request account deletion
                </a>
                <Link className="button button--outline" to="/login/customer">
                  Sign in to check your account
                </Link>
              </div>
              <p className="legal-fine-print">
                If you cannot access the registered email address, write to{" "}
                <a href="mailto:privacy@meroperks.com">privacy@meroperks.com</a> and explain the
                issue. We will request additional information to verify ownership.
              </p>
            </>
          ),
        },
        {
          id: "verification-and-timing",
          title: "Verification and timing",
          content: (
            <ol>
              <li>We acknowledge the request and verify that you control the account.</li>
              <li>
                We identify any unresolved redemptions, disputes, refundable balances, or legal
                holds that must be addressed.
              </li>
              <li>
                After verification, we delete or de-identify eligible data and confirm completion,
                normally within 30 days unless applicable law permits or requires more time.
              </li>
            </ol>
          ),
        },
        {
          id: "data-deleted",
          title: "Data deleted or de-identified",
          content: (
            <ul>
              <li>profile and optional demographic information;</li>
              <li>communication preferences and saved marketplace choices;</li>
              <li>active credentials, sessions, and reusable account identifiers;</li>
              <li>support content and other personal information that is no longer required;</li>
              <li>personal links to analytics that can safely be de-identified.</li>
            </ul>
          ),
        },
        {
          id: "data-retained",
          title: "Data that may be retained",
          content: (
            <>
              <p>
                We may retain limited records when required for tax, accounting, financial
                reconciliation, security, fraud prevention, legal claims, or regulatory compliance.
                This can include transaction and redemption ledgers, consent and verification
                evidence, security logs, and Business Owner audit records.
              </p>
              <p>
                Retained records are restricted, used only for those purposes, and removed or
                de-identified when the applicable requirement ends.
              </p>
            </>
          ),
        },
        {
          id: "effects-of-deletion",
          title: "What deletion means",
          content: (
            <p>
              Deletion ends access to the account. Unused Business Reward Points, Tier Points,
              rewards, and promotional Mero Credits will no longer be available. Purchased balances,
              if offered, are handled according to applicable refund rules and law. Account deletion
              does not automatically delete a separate customer or Business Owner role that uses the
              same email unless you request both.
            </p>
          ),
        },
        {
          id: "remove-app",
          title: "Removing the app is different",
          content: (
            <p>
              Uninstalling the Android app or clearing browser storage removes local data from that
              device but does not delete your Mero Perks account. Submit a deletion request using
              the button above to delete the account.
            </p>
          ),
        },
      ]}
    />
  );
}
